import React, { useContext, useEffect, useState } from 'react';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import PersonIcon from '@material-ui/icons/Person';
import Skeleton from '@material-ui/lab/Skeleton';
import { colors } from '@skyslope/mache';
import { useHistory, useParams } from 'react-router-dom';
import useUserInfo from '../../auth/useUserInfo';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../../common/launchDarkly';
import SkeletonTable from '../../common/loading/SkeletonTable';
import NavHeader from '../../common/navigation/NavHeader';
import { HeaderContext } from '../../context/header-context';
import useBottomScrollListener from '../../hooks/useBottomScrollListener';
import subGroupsEmpty from '../../images/subGroupsEmpty.svg';
import { getUsersGroups } from '../../lib/api/account/accountApi';
import type { OfficeUser, UserOffice } from '../../lib/api/prime/prime';
import { ResourceNames } from '../../lib/constants';
import { preferencesUrl } from '../../lib/environment-config';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import useDebounce from '../../lib/useDebounce';
import { checkIfTemplateIsSharedToUserInOffice, checkIfTemplateSharedToAnyUsersGroup } from '../../lib/utils';
import type { IRootState } from '../../store';
import { setLoading } from '../../store/pageFrame/actions';
import type { ISettings } from '../../store/pageFrame/types';
import * as templateCreateActions from '../../store/templateCreate/actions';
import type { ITemplate } from '../../store/templateCreate/template-types';
import * as actions from '../../store/templateSharing/actions';
import { getOfficesToSearch } from '../../store/templateSharing/saga';
import type { IShareRecord, ITemplateUser, SubGroup } from '../../store/templateSharing/types';
import FooterLinks from '../common/FooterLinks';
import NoResultsState from '../common/NoResultsState';
import Search from '../common/Search';
import Table from '../common/Table';
import EmptyState from './EmptyState';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 1000,
    width: '100%',
    margin: `${theme.spacing(7)}px ${theme.spacing(2)}px 0`,
    display: 'flex',
    flexDirection: 'column',
  },
  emptyStateRoot: {
    maxWidth: 1200,
    width: '100%',
    margin: `${theme.spacing(7)}px ${theme.spacing(2)}px 0`,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: colors.grey[700],
    marginBottom: theme.spacing(3),
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  templateCount: {
    display: 'flex',
    alignItems: 'center',
  },
  templateCountIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[500],
  },
  plusIcon: {
    margin: '-2px 0px -2px -6px',
    height: 18,
  },
  hotdogButton: {
    padding: 6,
    margin: -7,
  },
  foundSkeleton: {
    width: 200,
  },
  divider: {
    height: 1,
    width: '100%',
    marginTop: 40,
    marginBottom: 32,
    backgroundColor: colors.grey[400],
  },
  tabLinks: {
    display: 'flex',
  },
  tabLink: {
    padding: '6px 15px 4px 15px',
    margin: '4px 2px 1px 0',
    position: 'relative',
    '&:hover,&:focus': {
      cursor: 'pointer',
      backgroundColor: colors.blue[50],
      borderTopRightRadius: '15px',
      borderTopLeftRadius: '15px',
      outline: 'none',
    },
    userSelect: 'none',
  },
  tabLinkSelected: {
    backgroundColor: colors.blue[800],
    height: '3px',
    position: 'absolute',
    left: '0',
    bottom: '0',
    right: '0',
    margin: '0 14px',
    borderRadius: '2px',
  },
  subGroupCountIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[600],
  },
  subGroupCount: {
    display: 'flex',
    alignItems: 'center',
    color: colors.grey[800],
  },
  rowClassName: {
    maxWidth: '120px',
  },
}));

interface IProps {
  flags?: LaunchDarklyFlags;
  renderGroupsTable?: boolean;
  renderOfficeTable?: boolean;
}

interface IState {
  template: ITemplate;
  search: string;
  isFetching: boolean;
  settings?: ISettings;
  settingsFetched?: boolean;
  isLoading: boolean;
  users: ITemplateUser[];
  totalUsers: number;
  endReached: boolean;
  userExclusions: string[];
  sharedToAllParentGroups: boolean;
  subGroups: SubGroup[];
  sharedIds: IShareRecord[];
  orderByDesc: boolean;
  orderOfficeByDesc: boolean;
  sharesFetched: boolean;
  offices: UserOffice[];
  fetchingOfficeUsers: boolean;
  usersFromOffice: OfficeUser[];
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  template: state.templateCreate.template,
  search: state.templateManagement.search,
  isFetching: state.templateSharing.isFetching,
  settings: state.pageFrame.settings,
  settingsFetched: state.pageFrame.settingsFetched,
  isLoading: state.pageFrame.loading,
  users: state.templateSharing.users,
  totalUsers: state.templateSharing.totalUsers,
  endReached: state.templateSharing.endReached,
  userExclusions: state.templateSharing.userExclusions,
  sharedToAllParentGroups: state.templateSharing.sharedToAllParentGroups,
  subGroups: state.templateSharing.subGroups,
  sharedIds: state.templateSharing.sharedIds,
  orderByDesc: state.templateSharing.orderByDesc,
  orderOfficeByDesc: state.templateSharing.orderOfficeByDesc,
  sharesFetched: state.templateSharing.sharesFetched,
  offices: state.templateSharing.offices,
  fetchingOfficeUsers: state.templateSharing.fetchingOfficeUsers,
  usersFromOffice: state.templateSharing.usersFromOffices,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const TemplateShare = (props: IProps) => {
  const { flags, renderGroupsTable, renderOfficeTable } = props;
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const store: IState = useShallowSelector(selector);
  const dispatch = useDispatch();
  const {
    users,
    template,
    endReached,
    userExclusions,
    sharedToAllParentGroups,
    subGroups,
    sharedIds,
    orderByDesc,
    orderOfficeByDesc,
    sharesFetched,
    offices,
    fetchingOfficeUsers,
    usersFromOffice,
  } = store;
  const { oktaUserId } = useUserInfo() ?? {};
  const params = useParams<{ id: string }>();
  const headerContext = useContext(HeaderContext);
  const classes = useStyles();
  const history = useHistory();
  const [resetTableScroll, setResetTableScroll] = useState(false);
  const [showBottomLoader, setShowBottomLoader] = useState(false);
  const [firstRenderDone, setFirstRenderDone] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');
  const [currentTab, setCurrentTab] = useState(renderGroupsTable ? 'Groups' : renderOfficeTable ? 'Offices' : 'People');
  // This hook prevents the rendering of an empty state when the user backspaces the search input until it is empty
  const [alreadySearched, setAlreadySearched] = useState(false);
  const debouncedSearch = useDebounce(search, 200);
  const hideNavItems = flags && flags['hide-build-ui-elements'];
  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const showGroupsTable = flags && flags['template-group-sharing'];
  const officeSharingEnabled = flags?.['template-office-sharing'] ?? false;
  const [usersWithoutCurrentUser, setUsersWithoutCurrentUser] = useState<ITemplateUser[]>([]);

  const TABS = {
    People: {
      statuses: null,
      key: 'people',
      label: 'People',
    },
    Groups: {
      statuses: null,
      key: 'groups',
      label: 'Groups',
    },
    Offices: {
      statuses: null,
      key: 'offices',
      label: 'Offices',
    },
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSetSelectedStatus = (tab: string) => {
    if (currentTab !== tab) {
      setCurrentTab(tab);
    }
  };

  const pageTabs = (
    <div className={classes.tabLinks}>
      {Object.keys(TABS).map((tab, index) => {
        return (!showGroupsTable && tab === 'Groups') || (!officeSharingEnabled && tab === 'Offices') ? null : (
          <Typography
            key={tab}
            tabIndex={index + 1}
            className={`${classes.tabLink} `}
            onClick={(e: React.MouseEvent<HTMLParagraphElement>) => {
              handleSetSelectedStatus(tab);
              e.currentTarget.blur();
            }}
            variant={currentTab === tab ? 'body2' : 'body1'}
            data-spec={`${tab}-tab`}
          >
            {tab}
            {currentTab === tab ? <div className={classes.tabLinkSelected} /> : null}
          </Typography>
        );
      })}
    </div>
  );

  // First time the page loads, fetching users and share information
  // and making an async call to get user's id
  useEffect(() => {
    if (oktaUserId) {
      dispatch(setLoading(true));
      dispatch(
        actions.fetchTemplateShareInformation(20, 1, debouncedSearch, params.id, oktaUserId!, officeSharingEnabled)
      );
      dispatch(templateCreateActions.fetchTemplate(params.id));
    }
    return () => {
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [oktaUserId]);

  // moving from tabs, loads the header and content in pageTabs
  useEffect(() => {
    headerContext.setContent(
      <>
        <NavHeader
          labelName={`${template ? `Sharing ${template?.name}` : ''}`}
          breadcrumb={{ name: 'Templates', onClick: () => history.push('/templates') }}
          hideNavItems={hideNavItems}
          preferencesUrl={preferencesUrl}
          showPreferences={showPreferencesMenuItem}
          onSharingPage
          getUsersGroups={getUsersGroups}
          flags={flags}
        />
        {pageTabs}
      </>
    );
    return () => {
      headerContext.setContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [currentTab, template]);

  useEffect(() => {
    if (showBottomLoader) {
      setShowBottomLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.endReached]);

  const peopleTabColumns = [
    { key: 'check', label: '' },
    { key: 'name', label: 'Name', boldText: true },
    { key: 'email', label: 'Email' },
  ];

  const groupTabColumns = [
    { key: 'check', label: '' },
    { key: 'group', label: 'Group', boldText: true, sortKey: 'GROUP' },
    {
      key: 'people',
      label: 'People',
      cell: (subGroup: SubGroup) => (
        <Typography style={{ display: 'flex', marginLeft: '130px', color: colors.grey[700] }}>
          <PersonIcon />
          {subGroup.count}
        </Typography>
      ),
    },
  ];

  const officeTabColumns = [
    { key: 'check', label: '' },
    { key: 'officeName', label: 'Office', boldText: true, sortKey: 'OFFICE' },
  ];

  // changes in search input
  useEffect(() => {
    if (!oktaUserId) {
      return;
    }
    if (firstRenderDone) {
      setResetTableScroll(true);
      dispatch(setLoading(true));
      if (debouncedSearch === '') {
        // Reset default users
        dispatch(
          actions.fetchTemplateShareInformation(20, 1, debouncedSearch, params.id, oktaUserId, officeSharingEnabled)
        );
        setIsFetching(false);
      } else {
        setResetTableScroll(true);
        // The limit doesn't matter here, we always want to fetch up to 20 users the first time we search for a user
        dispatch(actions.fetchUsers(20, 1, debouncedSearch));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch]);

  const fetchMoreUsers = React.useCallback(() => {
    if (users.length && !isFetching) {
      setIsFetching(true);
      setResetTableScroll(true);
      setShowBottomLoader(true);
      // For people tab in share template, using ceiling since the same user is removed from the list
      dispatch(actions.fetchUsers(20, Math.ceil(users.length / 20) + 1, debouncedSearch, true));
      setResetTableScroll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch, users, isFetching]);

  // There is change in ShareIds
  // Updates: users, offices and usersFromOffice if there was a change in in officce selection
  useEffect(() => {
    if (!oktaUserId) {
      return;
    }
    if (
      !sharedToAllParentGroups &&
      firstRenderDone &&
      officeSharingEnabled &&
      !fetchingOfficeUsers &&
      sharedIds.some((s) => s.shareType === 'Office' || s.shareType === 'AllOffice')
    ) {
      dispatch(actions.fetchingOfficeUsers());
      const officeGuids = getOfficesToSearch(sharedIds);
      dispatch(actions.fetchOfficeUsers(officeGuids));
    }
    // all sharedIds were cleaned, un-checked all users
    if (sharedIds.length === 0) {
      const updatedUsers = users.map((u) => {
        return {
          ...u,
          isTemplateShared: false,
        };
      });
      dispatch(actions.updateUsers(updatedUsers));
    } else {
      const updatedUsers = users.map((u) => {
        const isUserExcluded = userExclusions.includes(u.oktaUserId);

        if (
          !isUserExcluded &&
          (sharedIds.some((s) => s.id === u.oktaUserId) ||
            (officeSharingEnabled && checkIfTemplateIsSharedToUserInOffice(u, usersFromOffice, userExclusions)) ||
            checkIfTemplateSharedToAnyUsersGroup(sharedIds, u.userGroups))
        ) {
          u.isTemplateShared = true;
        } else {
          u.isTemplateShared = false;
        }
        return u;
      });
      dispatch(actions.updateUsers(updatedUsers));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [sharedIds.length, usersFromOffice.length]);

  // There is a change in users
  // Sets UsersWithoutCurrentUser to render and clear all flags
  useEffect(() => {
    setUsersWithoutCurrentUser(users.filter((u) => u.id !== oktaUserId));
    setShowBottomLoader(false);
    setResetTableScroll(false);
    setIsFetching(false);
    setFirstRenderDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [users]);

  useBottomScrollListener(() => {
    if (!endReached) {
      fetchMoreUsers();
    }
  });

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleShareTemplateToUser = (user: ITemplateUser) => {
    user.isTemplateShared = !user.isTemplateShared;
    const sharedToASubGroup = checkIfTemplateSharedToAnyUsersGroup(sharedIds, user.userGroups);
    const sharedToAnOffice = officeSharingEnabled
      ? usersFromOffice.some((u) => u.oktaUserID === user.oktaUserId)
      : false;
    dispatch(
      actions.toggleTemplateShareToUser(
        user,
        template.name,
        params.id,
        sharedToAllParentGroups,
        sharedToASubGroup,
        sharedToAnOffice
      )
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleShareTemplateToSubGroup = (group: SubGroup, alreadyShared: boolean) => {
    dispatch(
      actions.toggleTemplateShareToSubGroup(
        template,
        group,
        alreadyShared,
        sharedToAllParentGroups,
        subGroups,
        sharedIds
      )
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleShareTemplateToOffice = (office: UserOffice, alreadyShared: boolean) => {
    dispatch(
      actions.toggleTemplateShareToOffice(
        office.officeGuid,
        office.officeName,
        template.id,
        template.name,
        alreadyShared,
        sharedIds
      )
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleShareTemplateToAllGroups = (templateAlreadyShared: boolean) => {
    dispatch(
      actions.toggleTemplateShareToAllUsersGroups(templateAlreadyShared, params.id, officeSharingEnabled, offices)
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSearchChange = (value: string) => {
    if (!alreadySearched && value !== '' && value.trim() !== '') {
      setAlreadySearched(true);
    }
    setSearch(value);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const toggleGroupSort = () => {
    dispatch(actions.toggleGroupsSort());
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const toggleOfficeSort = () => {
    dispatch(actions.toggleOfficesSort());
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const searchContent = () => {
    // If showing all users (no search), then the user is removed from the count
    // if it is a search result, remove users only at the time it was removed
    const totalUsersUpdate = Math.max(
      !debouncedSearch && store.totalUsers
        ? store.totalUsers - 1
        : usersWithoutCurrentUser.length === store.totalUsers
          ? store.totalUsers
          : store.totalUsers - 1,
      0
    );
    return (
      <>
        <div className={classes.divider} />
        <div className={classes.tableControls}>
          <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
            <PersonIcon className={classes.templateCountIcon} />
            {store.isFetching && !totalUsersUpdate
              ? 'FETCHING USERS...'
              : `${totalUsersUpdate} ${totalUsersUpdate === 1 ? 'PERSON' : 'PEOPLE'}`}
          </Typography>
        </div>
      </>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const displaySubGroupsCount = () => {
    return (
      <div className={classes.tableControls}>
        <Typography variant="overline" className={classes.subGroupCount}>
          <GroupWorkIcon className={classes.subGroupCountIcon} />
          {`${subGroups.length} GROUP${subGroups.length === 1 ? '' : 'S'}`}
        </Typography>
      </div>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const contentToDisplay = () => {
    if (!store.isLoading && store.settingsFetched && sharesFetched) {
      if (currentTab === 'People') {
        if (store.totalUsers === 0 && search) {
          return <NoResultsState resource={ResourceNames.TEMPLATE} />;
        }
        if (store.totalUsers) {
          return (
            <>
              {searchContent()}
              <Table
                columns={peopleTabColumns}
                rows={usersWithoutCurrentUser}
                showBottomLoader={showBottomLoader}
                resetScroll={resetTableScroll}
                checkboxOnClick={handleShareTemplateToUser}
                userExclusions={userExclusions}
                toggleTemplateShareToAllGroups={handleShareTemplateToAllGroups}
                isFetching={store.isFetching}
                sharedToAllParentGroups={sharedToAllParentGroups}
                currentTab={currentTab}
                onSharingPage
                sharedIds={sharedIds || []}
                subGroups={subGroups}
              />
            </>
          );
        }
      } else if (currentTab === 'Groups') {
        if (!subGroups.length) {
          return (
            <EmptyState
              image={subGroupsEmpty}
              heading="There are no groups, yet."
              subtitle="Contact your administrator for more information about groups."
            />
          );
        }
        return (
          <>
            {displaySubGroupsCount()}
            <Table
              columns={groupTabColumns}
              rows={subGroups}
              checkboxOnClick={handleShareTemplateToSubGroup}
              userExclusions={userExclusions}
              isFetching={store.isFetching}
              sharedToAllParentGroups={sharedToAllParentGroups}
              currentTab={currentTab}
              onSharingPage
              sharedIds={sharedIds}
              currentSortKey="GROUP"
              currentSortDesc={orderByDesc}
              onClickHeader={toggleGroupSort}
            />
          </>
        );
      } else {
        return (
          <Table
            columns={officeTabColumns}
            rows={offices}
            isFetching={store.isFetching}
            sharedIds={sharedIds}
            checkboxOnClick={handleShareTemplateToOffice}
            sharedToAllParentGroups={sharedToAllParentGroups}
            currentTab={currentTab}
            onSharingPage
            currentSortKey="OFFICE"
            currentSortDesc={orderOfficeByDesc}
            onClickHeader={toggleOfficeSort}
            cellClassName={classes.rowClassName}
          />
        );
      }
    }
    return (
      <>
        <div data-spec="skeleton-loading-table">
          {currentTab === 'People' && <div className={classes.divider} />}
          <div className={classes.tableControls}>
            <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
              {currentTab === 'Groups' ? (
                <GroupWorkIcon className={classes.subGroupCountIcon} />
              ) : (
                <PersonIcon className={classes.templateCountIcon} />
              )}
              <span className={classes.foundSkeleton}>
                <Skeleton animation="wave" />
              </span>
            </Typography>
          </div>
          <SkeletonTable columns={currentTab === 'Groups' ? groupTabColumns : peopleTabColumns} />
        </div>
      </>
    );
  };

  return (
    <div className={!subGroups.length ? classes.emptyStateRoot : classes.root}>
      {currentTab === 'People' ? <Search data-spec="searchBar" value={search} onChange={handleSearchChange} /> : ''}
      {contentToDisplay()}
      <FooterLinks showSocialIcons />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(TemplateShare);
